import { useState } from 'react';

import { Info, Send } from 'lucide-react';

import { Button, cn, Input, Textarea } from '@src/components/ui';
import { useInviteSendMutation } from '@src/query';

import { useInvitableRoles } from './bits';
import { Modal } from './controls';
import { Field } from './fields';


/** Split a comma/space/newline-separated string into trimmed, de-duped emails. */
function parseEmails(raw) {
  const seen = new Set();
  const result = [];
  for (const token of raw.split(/[\s,;]+/)) {
    const email = token.trim().toLowerCase();
    if (email && !seen.has(email)) {
      seen.add(email);
      result.push(email);
    }
  }
  return result;
}


function firstError(data) {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const value = data.email ?? data.detail ?? data.non_field_errors;
  if (Array.isArray(value)) {
    return value[0];
  }
  return typeof value === 'string' ? value : null;
}


function RoleOption({ value, label, description, selected, onSelect }) {
  return (
    <Button
      type="button"
      variant="ghost"
      aria-checked={selected}
      className={cn(
        'h-auto items-start justify-start gap-3 rounded-lg border p-3 text-left hover:bg-transparent',
        selected
          ? 'border-primary bg-primary-soft hover:bg-primary-soft'
          : 'border-hairline bg-surface-2 hover:bg-surface-2',
      )}
      role="radio"
      onClick={() => onSelect(value)}
    >
      <span
        className={cn(
          'mt-0.5 grid size-[18px] shrink-0 place-items-center rounded-full border-2',
          selected ? 'border-primary' : 'border-hairline-strong',
        )}
      >
        {selected
          ? <span className="bg-primary size-2.5 rounded-full" />
          : null}
      </span>

      <span className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold text-ink">{label}</span>
        <span className="text-table text-ink-subtle">{description}</span>
      </span>
    </Button>
  );
}

RoleOption.displayName = 'RoleOption';


/** Invite modal (#149, .pen CjDPp / r4BKZ): emails (one or many), role radio,
 * optional message. Sends one POST per email and aggregates per-email failures
 * so a seat-cap or duplicate on one address does not hide the rest. Built on the
 * shared Dialog-based `Modal` (focus trap, ESC, scroll-lock, bg-scrim). */
function InviteModal({ seller, atCap, onClose, onToast, onInvited }) {
  const invitableRoles = useInvitableRoles();
  const [emailsRaw, setEmailsRaw] = useState('');
  const [role, setRole] = useState('lister');
  const [message, setMessage] = useState('');
  const [failures, setFailures] = useState([]);
  const [emailError, setEmailError] = useState(null);

  const mutation = useInviteSendMutation();

  const submit = async () => {
    const emails = parseEmails(emailsRaw);
    setFailures([]);
    setEmailError(null);
    if (emails.length === 0) {
      setEmailError('Enter at least one email address.');
      return;
    }

    const failed = [];
    let sent = 0;
    for (const email of emails) {
      const payload = { email, role };
      if (message.trim()) {
        payload.message = message.trim();
      }
      try {
        await mutation.mutateAsync({ sellerId: seller.id, payload });
        sent += 1;
      }
      catch (error) {
        failed.push({ email, reason: firstError(error?.response?.data) || 'Could not send' });
      }
    }

    if (sent > 0) {
      onInvited();
      onToast(sent === 1 ? 'Invite sent' : `${sent} invites sent`);
    }
    if (failed.length === 0) {
      onClose();
      return;
    }
    setFailures(failed);
  };

  return (
    <Modal
      title="Invite a member"
      subtitle={`They'll get an email to join ${seller.name}.`}
      onClose={onClose}
      footer={(
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>

          <Button type="button" className="gap-1.5" onClick={submit} loading={mutation.isPending}>
            <Send className="size-[15px]" />
            Send invite
          </Button>
        </>
      )}
    >
      <Field
        label="Email"
        hint="· separate multiple with commas"
        error={emailError}
      >
        <Input
          value={emailsRaw}
          invalid={Boolean(emailError)}
          placeholder="sam@yard.com, alex@yard.com"
          onChange={(event) => setEmailsRaw(event.target.value)}
        />
      </Field>

      <span className="text-xs font-semibold uppercase tracking-[0.5px] text-ink-subtle">Role</span>

      <div className="flex flex-col gap-2.5">
        {invitableRoles.map(({ value, label, description }) => (
          <RoleOption
            key={value}
            value={value}
            label={label}
            selected={role === value}
            onSelect={setRole}
            description={description}
          />
        ))}
      </div>

      <Field label="Message (optional)">
        <Textarea
          value={message}
          className="h-[72px] resize-none"
          rows={3}
          onChange={(event) => setMessage(event.target.value)}
          placeholder="Add a personal note to the invite email…"
        />
      </Field>

      <div className="flex items-center gap-1.5 text-ink-subtle">
        <Info className="size-3.5" />
        <span className="text-xs">Owners are set by transfer, not invite.</span>
      </div>

      {atCap
        ? (
          <div className="rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-xs text-warning">
            All seats are in use. New invites will fail until you upgrade or free a seat.
          </div>
        )
        : null}

      {failures.length > 0
        ? (
          <div className="flex flex-col gap-1 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2">
            {failures.map((failure) => (
              <span key={failure.email} className="text-xs text-danger">
                {`${failure.email}: ${failure.reason}`}
              </span>
            ))}
          </div>
        )
        : null}
    </Modal>
  );
}

InviteModal.displayName = 'InviteModal';
export default InviteModal;
